import type { CSSProperties, JSX, ReactNode } from 'react';
import type { HoverCardContentProps } from '@radix-ui/react-hover-card';
import { Root, Trigger, Portal, Content } from '@radix-ui/react-hover-card';
import { styled, css, keyframes } from 'styled-components';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import type { Spacings } from '../../css/designTokens/spacing';

type StyledPreviewCardProps = {
  $unstyled?: boolean;
  $colorScheme: ColorSchemeTypes;
  $paddingSize: string;
};

const previewCardOpen = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const previewCardClose = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.96);
  }
`;

const StyledContent = styled(Content)<StyledPreviewCardProps>`
  --preview-card-animation-duration: var(--wui-motion-duration-03);
  --preview-card-animation-ease: var(--wui-motion-ease-out);

  z-index: var(--wui-zindex-popover);
  transform-origin: var(--radix-hover-card-content-transform-origin);
  animation-duration: var(--preview-card-animation-duration);
  animation-timing-function: var(--preview-card-animation-ease);
  will-change: transform, opacity;

  &[data-state='open'] {
    animation-name: ${previewCardOpen};
  }

  &[data-state='closed'] {
    animation-name: ${previewCardClose};
  }

  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  ${({ $unstyled, $paddingSize }) =>
    !$unstyled &&
    css`
      border-radius: var(--wui-border-radius-02);
      padding: var(--wui-${$paddingSize});
      max-width: var(--wui-preview-card-max-width, 320px);
      max-height: var(--wui-preview-card-max-height, auto);
      background-color: var(--wui-color-bg-surface);
      box-shadow: var(--wui-elevation-01);
      border: 1px solid var(--wui-color-border);
      overflow: auto;
    `}
`;

type ContentProps = Omit<
  HoverCardContentProps,
  | 'alignOffset'
  | 'arrowPadding'
  | 'asChild'
  | 'collisionPadding'
  | 'hideWhenDetached'
  | 'sideOffset'
>;

export type PreviewCardProps = ContentProps & {
  /**
   * The content of the preview card
   */
  children: ReactNode;

  /**
   * The maximum width of the preview card
   */
  maxWidth?: string;

  /**
   * The maximum height of the preview card
   */
  maxHeight?: string;

  /**
   * The [Link](?path=/docs/components-link--docs) that opens the preview card when hovered.
   * Preview cards should only be used on links—do not use on non-interactive elements (e.g. buttons or plain text).
   */
  trigger: JSX.Element;

  /**
   * If true, the preview card will use the unstyled Content component instead of StyledContent
   */
  unstyled?: boolean;

  /**
   * Specifies the internal padding of the preview card. Uses the design system's spacing scale.
   */
  paddingSize?: Spacings;

  /**
   * Duration in ms from when the mouse enters the trigger until the preview card opens
   */
  openDelay?: number;

  /**
   * Duration in ms from when the mouse leaves the trigger until the preview card closes
   */
  closeDelay?: number;

  colorScheme?: ColorSchemeTypes;

  /**
   * @ignore
   * Force the preview card to be open without user interaction (for debugging and CI)
   */
  forceOpen?: boolean;
};

/**
 * Preview cards reveal content on hover. **Everything in the card must also exist at the destination**—never put information only in the preview. This pattern is for sighted-only, so treat the card as a preview, not a source of truth.
 */
export const PreviewCard = ({
  children,
  trigger,
  maxWidth = '320px',
  maxHeight = 'auto',
  unstyled = false,
  paddingSize = 'space-04',
  openDelay = 700,
  closeDelay = 300,
  colorScheme = 'inherit',
  forceOpen,
  ...props
}: PreviewCardProps): JSX.Element => {
  const style = {
    '--wui-preview-card-max-width': maxWidth,
    '--wui-preview-card-max-height': maxHeight,
  } as CSSProperties;

  const rootProps: { open?: boolean } = {};

  if (forceOpen === true) {
    rootProps.open = true;
  }

  return (
    <Root
      closeDelay={closeDelay}
      openDelay={openDelay}
      {...rootProps}
    >
      <Trigger asChild={true}>{trigger}</Trigger>
      <Portal>
        <StyledContent
          $colorScheme={colorScheme}
          $paddingSize={paddingSize}
          sideOffset={8}
          {...props}
          $unstyled={unstyled}
          style={style}
        >
          <div>{children}</div>
        </StyledContent>
      </Portal>
    </Root>
  );
};

PreviewCard.displayName = 'PreviewCard';
